import { type KeyboardEvent, forwardRef, useRef, useState } from 'react';
import { Tooltip } from 'antd';
import { IoClose } from 'react-icons/io5';
import type { TagSource } from '@/models/tag';
import styles from './Tag.module.css';

const COMPACT_MAX = 14;

function formatTag(value: string, compact: boolean): string {
  const text = value.split('_').filter(Boolean).join(' ');
  return compact && text.length > COMPACT_MAX
    ? `${text.slice(0, COMPACT_MAX - 1).trimEnd()}…`
    : text;
}

interface TagProps {
  value: string;
  label?: string;
  compact?: boolean;
  onRemove?: (value: string) => void;
  onRemoveKeyDown?: (event: KeyboardEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  source?: TagSource;
  role?: string;
  className?: string;
}

const Tag = forwardRef<HTMLSpanElement, TagProps>(
  (
    {
      value,
      label,
      compact = false,
      onRemove,
      onRemoveKeyDown,
      disabled = false,
      source,
      role,
      className,
    },
    ref
  ) => {
    const labelRef = useRef<HTMLSpanElement>(null);
    const [truncated, setTruncated] = useState(false);

    const resolvedLabel = label ?? formatTag(value, compact);
    const showRemove = !!onRemove && !disabled;
    const tagClassName = [styles.tag, className].filter(Boolean).join(' ');
    const tooltip =
      label === undefined && (compact || truncated)
        ? formatTag(value, false)
        : undefined;

    return (
      <Tooltip title={tooltip}>
        <span
          ref={ref}
          role={role}
          className={tagClassName}
          data-testid={`tag-chip-${value}`}
          onMouseEnter={() => {
            const el = labelRef.current;
            if (el) setTruncated(el.scrollWidth > el.clientWidth);
          }}
        >
          {source === 'AI' && (
            <span
              className={`${styles.mark} ${styles.markAi}`}
              role="img"
              aria-label="Added by AI"
            >
              ✦
            </span>
          )}
          {source === 'MANUAL' && (
            <span
              className={`${styles.mark} ${styles.markManual}`}
              role="img"
              aria-label="Added manually"
            >
              ●
            </span>
          )}
          <span ref={labelRef} className={styles.label}>
            {resolvedLabel}
          </span>
          {showRemove && (
            <button
              type="button"
              className={styles.remove}
              data-testid={`tag-chip-remove-${value}`}
              onClick={() => onRemove?.(value)}
              onKeyDown={onRemoveKeyDown}
              aria-label={`Remove ${resolvedLabel}`}
            >
              <IoClose size={10} aria-hidden focusable="false" />
            </button>
          )}
        </span>
      </Tooltip>
    );
  }
);

Tag.displayName = 'Tag';

export default Tag;
